import { useEffect, useMemo, useState } from "react";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { formatTimestampOr } from "../lib/formatTime.js";
import { useToast } from "../components/Toast.js";
import { ConnectorEventLog } from "../components/ConnectorEventLog.js";
// ADR-0161 — one console home for the connector vocabularies, held to the hub
// by the G9 parity harness. This file used to keep its own copy of the dialect
// union and label map, and AdminTenantDetails.tsx kept another.
import {
  DIALECT_LABELS,
  KIND_LABELS,
  STATUS_LABELS,
  STATUS_PILL_CLASSES,
  type ConnectorDialect,
  type ConnectorKind,
  type ConnectorStatus,
} from "../lib/connectorVocab.js";

type ConnectorRow = {
  id: string;
  name: string;
  dialect: ConnectorDialect | null;
  /** ADR-0161 — read-only here; kind is a platform-operator decision. */
  kind: ConnectorKind;
  status: ConnectorStatus;
  status_reasons: string[];
  last_heartbeat_at: string | null;
  last_poll_at: string | null;
  last_execution_assigned_at: string | null;
  last_execution_completed_at: string | null;
  last_execution_status: "succeeded" | "failed" | null;
};

export function TenantAdminConnectors() {
  const toast = useToast();
  const [rows, setRows] = useState<ConnectorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingRestartId, setPendingRestartId] = useState<string | null>(null);
  const [restartNotice, setRestartNotice] = useState<string | null>(null);

  async function load(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ connectors: ConnectorRow[] }>("/api/tenant/connectors");
      setRows(data.connectors);
      setSelectedId((prev) => (prev && data.connectors.some((row) => row.id === prev) ? prev : data.connectors[0]?.id ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    let last = 0;
    function maybeRefresh() {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 15_000) return;
      last = now;
      void load({ silent: true });
    }
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
  }, []);

  const selected = useMemo(() => rows.find((row) => row.id === selectedId) ?? null, [rows, selectedId]);

  async function requestRestart(connector: ConnectorRow) {
    const ok = window.confirm(`Restart '${connector.name}'? This will briefly interrupt executions.`);
    if (!ok) return;
    setPendingRestartId(connector.id);
    setError(null);
    try {
      const data = await apiFetch<{ command: { status: string } }>(`/api/tenant/connectors/${encodeURIComponent(connector.id)}/restart`, {
        method: "POST"
      });
      toast.push({ kind: "success", message: "Restart requested." });
      if (data.command.status !== "acked") {
        setRestartNotice("Restart requested (waiting for connector).");
      } else {
        setRestartNotice(null);
      }
      await load({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPendingRestartId(null);
    }
  }

  return (
    <PageLayout title="Connectors">
      {error ? <ErrorState detail={error} /> : null}
      {loading ? <LoadingState /> : null}

      {!loading ? (
        <div style={{ display: "grid", gap: 12 }}>
          <div className="card tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Kind</th>
                  <th>Dialect</th>
                  <th>Last heartbeat</th>
                  <th>Last poll</th>
                  <th>Last execution</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="linkBtn"
                        onClick={() => setSelectedId(row.id)}
                        title="View logs"
                      >
                        {row.name}
                      </button>
                    </td>
                    <td>
                      <span className={STATUS_PILL_CLASSES[row.status]}>{STATUS_LABELS[row.status]}</span>
                    </td>
                    {/* Kind is total, so "—" can never appear here (unlike dialect). */}
                    <td className="muted">{KIND_LABELS[row.kind]}</td>
                    <td className="muted">{row.dialect ? DIALECT_LABELS[row.dialect] : "—"}</td>
                    <td className="muted">{formatTimestampOr(row.last_heartbeat_at, "Never")}</td>
                    <td className="muted">{formatTimestampOr(row.last_poll_at, "Never")}</td>
                    <td className="muted">{formatTimestampOr(row.last_execution_completed_at ?? row.last_execution_assigned_at, "Never")}</td>
                    <td>
                      <div className="actions" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="btn btnSm"
                          type="button"
                          onClick={() => void requestRestart(row)}
                          disabled={pendingRestartId === row.id}
                        >
                          Restart
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="muted">
                      No connectors yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {restartNotice ? <div className="muted">{restartNotice}</div> : null}
          {selected ? (
            <ConnectorEventLog
              connectorId={selected.id}
              connectorName={selected.name}
              connectorStatus={selected.status}
              apiBasePath="/api/tenant/connectors"
            />
          ) : null}
        </div>
      ) : null}
    </PageLayout>
  );
}
